import { format } from 'date-fns';
import { pathToRegexp } from 'path-to-regexp';
import { lifecycle } from 'recompose';
import * as R from 'ramda';

const isReactNativeWebView = !!window.ReactNativeWebView;

const formatDateToFilterFormat = R.memoizeWith(R.identity, value =>
  format(+value, 'yyyy-MM-dd'),
);

export const formatDateToISO = R.memoizeWith(R.identity, value =>
  format(+value, "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'"),
);

export const isReactNativeAppWebView =
  isReactNativeWebView && process.env.REACT_APP_TYPE === 'NATIVE';

export const isReactNativeAceWebView =
  isReactNativeWebView && process.env.REACT_APP_TYPE === 'WEB';

const isTruthyLowerCase = R.pipe(
  R.toLower,
  R.equals('true'),
);

export const isValidNumber = R.both(R.is(Number), R.complement(R.equals(NaN)));

export const findRouteIndexByPathname = (pathname, routes) =>
  R.findIndex(({ route }) => matchRouteByPathname(pathname, route))(routes);

export const getLanguageFromLocale = R.cond([
  [R.equals('en'), R.always('en-US')],
  [R.equals('zh'), R.always('zh-CN')],
  [R.T, R.identity],
]);

export const getLocaleFromLanguage = R.cond([
  [R.equals('en-US'), R.always('en')],
  [R.T, R.identity],
]);

export const getRouteNavigationParams = R.curryN(
  3,
  (keysMap, sortKeysMap, obj) => {
    const { filter = {}, sortBy, ...rest } = obj;

    const navigationParams = {
      ...mergeDeepRightAll(
        renameKeys({
          page: 'PageNum',
          pageSize: 'PageSize',
          generationLevel: 'Generation',
        })(rest),
        renameKeys(keysMap)(filter),
        sortBy && sortKeysMap
          ? R.pipe(
              R.invertObj,
              renameKeys(sortKeysMap),
              R.invertObj,
            )({ SortBy: sortBy })
          : {},
      ),
    };

    return navigationParams;
  },
);

export const getRouteNavigationParamsWithFormattedDate = R.curryN(
  3,
  (keysMap, sortKeysMap, obj) => {
    let { filter = {}, ...rest } = obj;

    filter = {
      ...filter,
      date:
        filter.date &&
        R.map(
          // @TODO: update when API would be ready
          // formatDateToISO,
          formatDateToFilterFormat,
          filter.date,
        ),
    };

    return getRouteNavigationParams(keysMap, sortKeysMap, { filter, ...rest });
  },
);

export const matchRouteByPathname = (pathname, route) =>
  pathToRegexp(route).test(pathname);

export const matchRoutesByPathname = (pathname, routes) =>
  !!routes.length &&
  R.any(route => route && matchRouteByPathname(pathname, route))(routes);

export const mergeDeepRightAll = R.unapply(R.reduce(R.mergeDeepRight, {}));

export const parseBool = R.cond([
  [R.is(Boolean), R.identity],
  [R.isNil, R.F],
  [R.T, isTruthyLowerCase],
]);

export const renameKeys = R.curry((keysMap, obj) =>
  R.reduce(
    (acc, key) => {
      const mappedKey = keysMap[key] || key;

      if (Array.isArray(mappedKey)) {
        return R.pipe(
          R.assoc(mappedKey[0], R.path([key, 0], obj)),
          R.assoc(mappedKey[1], R.path([key, 1], obj)),
        )(acc);
      }

      return R.assoc(mappedKey, obj[key], acc);
    },
    {},
    R.keys(obj),
  ),
);

export const renameTokens = renameKeys({
  id_token: 'idToken',
  access_token: 'accessToken',
  refresh_token: 'refreshToken',
});

export const scrollTabs = ({ index, navRef, scrollRef, tabs }) => {
  if (R.isNil(index) || index === -1) {
    return;
  }

  const navElement = navRef.current;
  const navElementClientRect = navElement.getBoundingClientRect();
  const scrollElement = scrollRef.current;
  const tabElement = tabs[index].ref.current;
  const tabElementClientRect = tabElement.getBoundingClientRect();
  const tabElementScrollEnd =
    tabElementClientRect.left + tabElement.offsetWidth;
  const tabElementStyle = window.getComputedStyle(tabElement);

  let scrollLeft;

  // check whether tab is scrolled left
  if (tabElementClientRect.left < 0) {
    // check whether selected tab is not the first one=
    // ? scroll to the begging of tab
    // : scroll to the begging of navigation
    scrollLeft = index
      ? Math.abs(navElementClientRect.left) -
        Math.abs(tabElementClientRect.left) -
        parseFloat(tabElementStyle.marginLeft) / 2
      : 0;
  } else {
    // check whether selected tab is the last one
    if (index === tabs.length - 1) {
      // scroll to the end of navigation parent container
      scrollLeft =
        scrollElement.scrollWidth - scrollElement.parentNode.scrollWidth;
    } else {
      // check whether selected tab is in viewport but it does not fit it
      if (tabElementScrollEnd > window.innerWidth) {
        // scroll to the end of tab
        scrollLeft =
          Math.abs(navElementClientRect.left) +
          tabElementScrollEnd -
          window.innerWidth +
          parseFloat(tabElementStyle.marginLeft) / 2;
      }
    }
  }

  if (!R.isNil(scrollLeft)) {
    scrollElement.scroll(scrollLeft, 0);
  }
};

/**
 * @description this is fix to prevent Google Translate plugin for crash https://github.com/facebook/react/issues/11538#issuecomment-417504600
 */
export const withGoogleTranslateFix = lifecycle({
  componentDidMount() {
    if (typeof Node === 'function' && Node.prototype) {
      const originalRemoveChild = Node.prototype.removeChild;
      Node.prototype.removeChild = function(child) {
        if (child.parentNode !== this) {
          if (console) {
            console.error(
              'Cannot remove a child from a different parent',
              child,
              this,
            );
          }
          return child;
        }
        return originalRemoveChild.apply(this, arguments);
      };

      const originalInsertBefore = Node.prototype.insertBefore;
      Node.prototype.insertBefore = function(newNode, referenceNode) {
        if (referenceNode && referenceNode.parentNode !== this) {
          if (console) {
            console.error(
              'Cannot insert before a reference node from a different parent',
              referenceNode,
              this,
            );
          }
          return newNode;
        }
        return originalInsertBefore.apply(this, arguments);
      };
    }
  },
});
